import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Generate preview of weekly squad dates client-side (matches backend logic)
function previewWeeklyDates(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate) return [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return [];

  const dates: string[] = [];
  const current = new Date(start);
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 7);
  }
  return dates;
}

const CreateExpressLeague: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', venue: '', startDate: '', endDate: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ leagueId: number; leagueName: string; squadCount: number } | null>(null);

  const squadPreview = useMemo(
    () => previewWeeklyDates(form.startDate, form.endDate),
    [form.startDate, form.endDate]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.venue.trim() || !form.startDate || !form.endDate) {
      setError(t('bookie.createLeague.allFieldsRequired'));
      return;
    }
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      setError(t('bookie.createLeague.endBeforeStart'));
      return;
    }
    if (squadPreview.length === 0) {
      setError(t('bookie.createLeague.noSquads'));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/bookie-side-action/express-league`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          venue: form.venue.trim(),
          startDate: form.startDate,
          endDate: form.endDate,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.error || t('common.error'));
        return;
      }
      setSuccess({ leagueId: data.leagueId, leagueName: data.leagueName, squadCount: data.squadCount });
    } catch {
      setError(t('auth.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({ name: '', venue: '', startDate: '', endDate: '' });
    setSuccess(null);
    setError('');
  };

  if (!user) return null;

  // ── Success Screen ──────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 sm:p-12 max-w-lg w-full text-center">
          <div className="text-6xl mb-6">🎯</div>
          <h2 className="text-3xl font-bold text-white mb-3">{t('bookie.createLeague.successTitle')}</h2>
          <p className="text-emerald-200 mb-2">
            <strong className="text-white">"{success.leagueName}"</strong>
          </p>
          <div className="bg-emerald-900/40 rounded-xl p-4 mb-6 border border-emerald-400/30">
            <p className="text-emerald-100 text-lg font-semibold mb-1">
              {t('bookie.createLeague.squadsCreated', { count: success.squadCount })}
            </p>
            <p className="text-emerald-300 text-sm">{t('bookie.createLeague.successMessage')}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/bookie/dashboard')}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-xl transition duration-200"
            >
              {t('bookie.createLeague.backToDashboard')}
            </button>
            <button
              onClick={handleReset}
              className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-3 px-6 rounded-xl transition duration-200"
            >
              {t('bookie.createLeague.createAnother')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form Screen ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/bookie/dashboard')}
                className="text-emerald-200 hover:text-white transition-colors duration-200 flex items-center space-x-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="text-sm hidden sm:inline">{t('common.back')}</span>
              </button>
              <div className="w-px h-6 bg-white/20" />
              <h1 className="text-lg sm:text-xl font-bold text-white">{t('bookie.createLeague.title')}</h1>
            </div>
            <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">{user.name.charAt(0).toUpperCase()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 sm:p-8">
            <div className="mb-6">
              <p className="text-emerald-200 text-sm">{t('bookie.createLeague.subtitle')}</p>
              <p className="text-emerald-300 text-xs mt-1 bg-emerald-900/40 rounded-lg px-3 py-2 border border-emerald-400/30 mt-3">
                ℹ️ {t('bookie.createLeague.descriptionNote')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* League Name */}
              <div>
                <label className="block text-white font-semibold text-sm mb-2">
                  {t('bookie.createLeague.leagueName')} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t('bookie.createLeague.leagueNamePlaceholder')}
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-emerald-300/50 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                  required
                />
              </div>

              {/* Venue */}
              <div>
                <label className="block text-white font-semibold text-sm mb-2">
                  {t('bookie.createLeague.venue')} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="venue"
                  value={form.venue}
                  onChange={handleChange}
                  placeholder={t('bookie.createLeague.venuePlaceholder')}
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-emerald-300/50 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                  required
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-semibold text-sm mb-2">
                    {t('bookie.createLeague.startDate')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition [color-scheme:dark]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold text-sm mb-2">
                    {t('bookie.createLeague.endDate')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={form.endDate}
                    onChange={handleChange}
                    min={form.startDate || undefined}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition [color-scheme:dark]"
                    required
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-900/40 border border-red-400/50 text-red-200 rounded-xl px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || squadPreview.length === 0}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl transition duration-200 transform hover:scale-105"
              >
                {submitting ? t('bookie.createLeague.creating') : t('bookie.createLeague.submitButton')}
              </button>
            </form>
          </div>

          {/* Squad Preview Panel */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 sm:p-8">
            <h3 className="text-xl font-bold text-white mb-2">{t('bookie.createLeague.squadsPreview')}</h3>

            {squadPreview.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-emerald-400">
                <div className="text-4xl mb-3">📅</div>
                <p className="text-sm text-center text-emerald-300">{t('bookie.createLeague.noSquads')}</p>
              </div>
            ) : (
              <>
                <p className="text-emerald-300 text-sm mb-4">
                  {t('bookie.createLeague.squadsWillBeGenerated', { count: squadPreview.length })}
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {squadPreview.map((date, i) => (
                    <div
                      key={date}
                      className="flex items-center space-x-3 bg-emerald-900/30 border border-emerald-400/20 rounded-lg px-3 py-2"
                    >
                      <span className="w-7 h-7 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-white text-sm font-semibold">Week {i + 1}</p>
                        <p className="text-emerald-300 text-xs">{new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateExpressLeague;
